import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import {
  getFromLocalStorage,
  LOCAL_STORAGE_USER,
  METRIC_SETTINGS,
  storeDirectlyIntoLocalStorage,
} from "utils/localStorage";
import { excludeById } from "utils/utilFunctions";
import { getProjectIdFromPath } from "utils/getProjectIdFromPath";
import { history } from "utils/history";
import type { Service } from "../graphql/generated";
import type { MetricsPollingSettings } from "../pages/project/service/monitoring/metrics/utils";
import { commonStore } from "./commonStore";
import { productStore } from "./productStore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);

const localMetricsSettings = getFromLocalStorage<
  Record<string, MetricsPollingSettings>
>(METRIC_SETTINGS, LOCAL_STORAGE_USER, {});

type InitialPassword = { id: string; initialPassword: string };

export class ServiceStore {
  serviceId = "";
  isServiceConverting = false;

  selectedMetricsSettings: { [key: string]: MetricsPollingSettings } =
    localMetricsSettings;

  // The password the create mutation returned, per service. Memory only: it is
  // shown once in the post-create flow and must never reach localStorage, so a
  // reload drops it by design.
  initialPasswords: InitialPassword[] = [];

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.selectedMetricsSettings,
      (time) => {
        storeDirectlyIntoLocalStorage(
          METRIC_SETTINGS,
          toJS(time),
          LOCAL_STORAGE_USER,
        );
      },
    );

    // A project switch makes the selected service incorrect. Reset it, so
    // that an old serviceId does not go to the new project. Routes outside a
    // project do not change the last project.
    let lastProjectId = getProjectIdFromPath(history.location.pathname);
    history.subscribe(({ location }) => {
      const projectId = getProjectIdFromPath(location.pathname);
      if (!projectId || projectId === lastProjectId) return;
      lastProjectId = projectId;
      runInAction(() => {
        this.serviceId = "";
      });
    });
  }

  setService = ({ id }: { id: string }) => {
    runInAction(() => {
      this.serviceId = id;
    });
    commonStore.setIsDataAvailable(true);
  };

  setMetricsSettings = (metricSettings: MetricsPollingSettings) => {
    runInAction(() => {
      this.selectedMetricsSettings = {
        ...this.selectedMetricsSettings,
        [this.serviceId]: metricSettings,
      };
    });
  };

  setInitialPassword = ({
    id,
    initialPassword,
  }: {
    id: string;
    initialPassword: string;
  }) => {
    runInAction(() => {
      this.initialPasswords = excludeById({
        items: this.initialPasswords,
        id,
      }).concat([{ id, initialPassword }]);
    });
  };

  registerCreatedService = ({
    service,
    initialPassword,
  }: {
    service: Service;
    initialPassword: string;
  }) => {
    this.setInitialPassword({ id: service.id, initialPassword });
    productStore.resetServices();
    this.setService(service);
  };

  resetService = () => {
    runInAction(() => {
      this.serviceId = "";
    });
  };

  setIsServiceConverting = (isServiceConverting: boolean) => {
    this.isServiceConverting = isServiceConverting;
  };

  clearStore = () => {
    runInAction(() => {
      this.initialPasswords = [];
    });
  };
}

export const serviceStore = new ServiceStore();
